import { DollarSign, Gift, Ticket, TrendingUp, Calendar, CheckCircle, Rocket } from "lucide-react";

export function DashboardRevenueTab({ revenue, myEvents, handleBoostEvent, boostLoading }) {
  return (
    <div className="space-y-4" data-testid="revenue-section">
      {revenue ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
              <DollarSign className="w-5 h-5 text-emerald-500 mb-1.5" />
              <p className="font-mono-credits font-bold text-xl text-card-foreground">
                {((revenue.ticket_revenue || 0) / 100).toFixed(0)} EUR
              </p>
              <p className="text-[10px] text-muted-foreground">Revenus billetterie</p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
              <Gift className="w-5 h-5 text-[#FF6B35] mb-1.5" />
              <p className="font-mono-credits font-bold text-xl text-card-foreground">
                {revenue.auction_earnings || 0} cr
              </p>
              <p className="text-[10px] text-muted-foreground">Gains encheres</p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
              <Ticket className="w-5 h-5 text-[#7000FF] mb-1.5" />
              <p className="font-mono-credits font-bold text-xl text-card-foreground">
                {revenue.total_tickets_sold || 0}
              </p>
              <p className="text-[10px] text-muted-foreground">Billets vendus</p>
            </div>
            <div className="bg-card border border-emerald-200 rounded-2xl p-4 shadow-sm">
              <TrendingUp className="w-5 h-5 text-amber-500 mb-1.5" />
              <p className="font-mono-credits font-bold text-xl text-card-foreground">
                {((revenue.pending_payout || 0) / 100).toFixed(0)} EUR
              </p>
              <p className="text-[10px] text-muted-foreground">En attente de versement</p>
            </div>
          </div>

          {(revenue.events || []).length > 0 && (
            <div>
              <h3 className="font-heading font-bold text-sm text-card-foreground mb-2">Par evenement</h3>
              <div className="space-y-2">
                {revenue.events.map((ev) => (
                  <div key={ev.event_id} className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3 shadow-sm">
                    <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/15 flex items-center justify-center shrink-0">
                      <Calendar className="w-5 h-5 text-[#FF6B35]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold text-sm text-card-foreground truncate">{ev.title}</p>
                      <p className="text-[10px] text-muted-foreground">{ev.date} - {ev.tickets_sold} billets</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-mono-credits font-bold text-sm text-emerald-500">{(ev.revenue / 100).toFixed(0)} EUR</p>
                      <p className="text-[10px] text-muted-foreground">com: {(ev.fees / 100).toFixed(0)} EUR</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(revenue.payouts || []).length > 0 && (
            <div>
              <h3 className="font-heading font-bold text-sm text-card-foreground mb-2">Versements recus</h3>
              <div className="space-y-2">
                {revenue.payouts.map((p) => (
                  <div key={p.id} className="bg-card border border-emerald-200 rounded-2xl p-3 flex items-center gap-3 shadow-sm">
                    <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-card-foreground">{p.event_title}</p>
                      <p className="text-[10px] text-muted-foreground">{new Date(p.processed_at).toLocaleDateString("fr-FR")}</p>
                    </div>
                    <p className="font-mono-credits font-bold text-sm text-emerald-500">{(p.artist_payout / 100).toFixed(2)} EUR</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <DollarSign className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Aucune donnee de revenus</p>
        </div>
      )}

      <div>
        <h3 className="font-heading font-bold text-sm text-card-foreground mb-2 flex items-center gap-1.5">
          <Rocket className="w-4 h-4 text-[#FF6B35]" />
          Booster un evenement
        </h3>
        <p className="text-[10px] text-muted-foreground mb-3">50 credits = 24h de visibilite prioritaire dans le feed Decouvrir</p>
        {myEvents.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Aucun evenement a venir a booster</p>
        ) : (
          <div className="space-y-2">
            {myEvents.map((e) => {
              const isBoosted = e.boost_expires_at && new Date(e.boost_expires_at) > new Date();
              return (
                <div key={e.id} className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3 shadow-sm">
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-sm text-card-foreground truncate">{e.title}</p>
                    <p className="text-[10px] text-muted-foreground">{e.date}{e.location ? ` - ${e.location}` : ""}</p>
                  </div>
                  {isBoosted ? (
                    <span className="text-[10px] px-3 py-1.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] font-bold border border-[#FF6B35]/20">
                      Booste
                    </span>
                  ) : (
                    <button
                      onClick={() => handleBoostEvent(e.id)}
                      disabled={boostLoading === e.id}
                      className="flex items-center gap-1.5 bg-[#FF6B35] text-white font-bold px-4 py-2 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
                      data-testid={`boost-event-${e.id}`}
                    >
                      <Rocket className="w-3.5 h-3.5" />
                      {boostLoading === e.id ? "..." : "50 cr"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
